import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslate } from '@tolgee/react';
import { useLogs } from '@/api/services/globalStats';
import CustomSkeleton from '@/components/Common/CustomSkeleton';
import { GameLogResponseType } from '@/types/GameLog/res';
import styles from './index.module.scss';
import classNames from 'classnames';
import useWebSocket from '@/hooks/useWebSocket';

// Memoized message item component
const MessageItem = React.memo(({ item }: { item: GameLogResponseType }) => {
  const highlightKeywords = useCallback((description: string) => {
    let highlighted = description;

    // Highlight numbers with LRT, and words "liberated" and "minted"
    const regex1 = /(\d+(\.\d+)?)\s*(LRT)|(liberated|minted)/gi;
    highlighted = highlighted.replace(regex1, (match) => {
      return `<span style="color:#70F800; font-size:16px;">${match}</span>`;
    });

    // Highlight the word after "User " and before the next space
    const regex2 = /User\s+(\w+)/g;
    highlighted = highlighted.replace(regex2, (match, p1) => {
      return `User <span style="color:#70F800; font-size:16px;">${p1}</span>`;
    });

    return highlighted;
  }, []);

  const escapeHTML = useCallback((str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'), []);

  const highlightedDescription = useMemo(() => {
    const escapedDescription = escapeHTML(item.Description);
    return highlightKeywords(escapedDescription);
  }, [item.Description, escapeHTML, highlightKeywords]);

  return (
    <motion.div
      key={item.Id}
      className={classNames(styles['typewriter-container'], 'p-2 my-2 bg-[#D9D9D91A]/[0.1] rounded-[20px]')}
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <span
        className={styles['typewriter-text']}
        dangerouslySetInnerHTML={{
          __html: `<span style="color:#FFFFFF; font-size:14px;">${highlightedDescription}</span><span style="color:#70F800; font-size:12px;"> Just Now</span>`,
        }}
      />
    </motion.div>
  );
});

MessageItem.displayName = 'MessageItem';

const CommandCenterGamelog: React.FunctionComponent = React.memo(() => {
  const { t } = useTranslate('commandCenter');
  const [messagesList, setMessagesList] = useState<GameLogResponseType[]>([]);
  const { logs, isLoading } = useLogs();
  const { messages: lastMessage } = useWebSocket<GameLogResponseType>('wss://event-message.landrocker.io');

  useEffect(() => {
    if (logs) {
      setMessagesList(logs.notif);
    }
  }, [logs]);

  useEffect(() => {
    if (!isLoading && lastMessage !== null) {
      setMessagesList((prevMessages) => {
        const newMessages = [lastMessage, ...prevMessages];
        return newMessages.slice(0, 100); // Limit to 100 messages for performance
      });
    }
  }, [lastMessage, isLoading]);

  if (Array.isArray(messagesList) && messagesList.length === 0) {
    return (
      <div className='h-full w-full rounded-md bg-[#3C3B3B] bg-opacity-40 col-span-12 xl:mt-0'>
        <div className='text-center p-2 uppercase'>{t('CENTER_SECTION.noGameLog')}</div>
      </div>
    );
  }

  return (
    <div className='relative col-span-12 md:col-span-6 xl:col-span-2 bg-[#3C3B3B]/[0.4] rounded-[10px] px-2 overflow-hidden -mt-1 xl:mt-1'>
      {/* Shadow gradient on the right side */}
      <div className='absolute inset-y-0 right-0 w-[60px] bg-gradient-to-l from-[#1A2F36] to-[#3C3B3B]/[0.001] pointer-events-none' />
      <div className='flex flex-nowrap gap-4 items-center overflow-hidden'>
        {isLoading && <CustomSkeleton width='100%' height={110} />}
        <AnimatePresence>
          {messagesList.slice(0, 10).map((item) => (
            <MessageItem key={item.Id} item={item} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
});

CommandCenterGamelog.displayName = 'CommandCenterGamelog';

export default CommandCenterGamelog;
